import type { BlockyTimeDB } from '@/db/schema'
import type { EntryRow } from '@/db/types'
import { restore, type ChangeSet } from '@/core/repository/entryRepository'

/**
 * Undo and redo, by inverting the ChangeSets every mutation returns.
 *
 * Not persisted: the stacks are cleared when the app closes. Undoing across a
 * relaunch would mean replaying operations against data that may have changed
 * on the other device in the meantime, which is a far worse failure than
 * simply not offering it.
 *
 * All writes go back through entryRepository, so the slot invariant is still
 * guarded exactly once -- CLAUDE.md rule 2.
 */

export const UNDO_DEPTH = 20

/** Rows created by an operation, i.e. present after but not before. */
function createdIds(change: ChangeSet): string[] {
  const beforeIds = new Set(change.before.map((r) => r.id))
  return change.after.filter((r) => !beforeIds.has(r.id)).map((r) => r.id)
}

/** Rows an operation removed entirely, i.e. present before but not after. */
function removedRows(change: ChangeSet): EntryRow[] {
  const afterIds = new Set(change.after.map((r) => r.id))
  return change.before.filter((r) => !afterIds.has(r.id))
}

/**
 * Reverses an operation.
 *
 * Restores whatever existed beforehand, and tombstones anything the operation
 * brought into being. Marking an empty block has an empty `before`, so undoing
 * it is purely the second half.
 */
export async function applyUndo(change: ChangeSet, db?: BlockyTimeDB): Promise<void> {
  await restore(change.before, createdIds(change), db)
}

/** Re-applies an operation. Exactly symmetric with applyUndo. */
export async function applyRedo(change: ChangeSet, db?: BlockyTimeDB): Promise<void> {
  await restore(
    change.after,
    removedRows(change).map((r) => r.id),
    db,
  )
}

/** True when an operation changed nothing, so it should not enter the stack. */
export function isEmptyChange(change: ChangeSet): boolean {
  return change.before.length === 0 && change.after.length === 0
}

/** Adds to a stack, discarding the oldest entry beyond the depth limit. */
export function pushBounded<T>(stack: readonly T[], item: T, depth = UNDO_DEPTH): T[] {
  const next = [...stack, item]
  return next.length > depth ? next.slice(next.length - depth) : next
}
